<template>
  <div class="cliente-form">
    <form @submit.prevent="handleSubmit">
      <!-- AI Completion (Version 1 only) -->
      <div v-if="usuarioVersao == 1" class="ai-completion-section">
        <object-completion
          elem-id="clienteCompletionComponent"
          :label="t('Completar com IA')"
          v-model="clienteCompletionData"
          :get-json-schema-to-fill="retornaCliente"
          :extra-informations-object="extraInformationsObject"
          :success-callback="inferSuccessCallback"
          :error-callback="inferErrorCallback"
          api-endpoint="/index.php/completions/completions/infer"
          :placeholder="t('Digite o conteúdo para completar')"
          :description="t('Use a IA para completar os dados do cliente. Digite, cole conversas ou imagens relevantes para a IA ajudar a completar o formulário.')"
          :debug="false"
          :cancel-label="t('Cancelar')"
          :send-label="t('Enviar')"
          :ng-required="true">
        </object-completion>
      </div>

      <!-- General Information -->
      <box type="primary" :title="t('Informações gerais')" elem-id="boxInformacoesGerais">
        <franchise-select
          :label="t('Franquia')"
          :placeholder="t('Escolha a franquia')"
          elem-id="cliente_franquia_id"
          v-model="formData.cliente_franquia_id"
          :callback="handleFranquiaChange"
          @franchise-data="handleFranquiaData">
        </franchise-select>

        <input-text
          elem-id="cliente_nome"
          :ng-required="true"
          :validation="validateNome"
          v-model="formData.cliente_nome"
          :label="t('Nome do cliente')"
          :placeholder="t('Nome do cliente')"
          data-testid="cliente-nome-input">
        </input-text>

        <!-- Documento dinâmico baseado no país da franquia -->
        <!-- Seletor de tipo de documento (visível apenas quando há múltiplos tipos) -->
        <select-list
          v-if="hasMultipleDocumentTypes"
          :label="t('Tipo de documento')"
          :placeholder="t('Escolha o tipo')"
          elem-id="cliente_document_type"
          v-model="formData.cliente_document_type"
          :itens-select="documentTypeOptions"
          :callback="handleDocumentTypeChange"
          data-testid="cliente-document-type-select">
        </select-list>
        <input-document
          elem-id="cliente_cpf"
          v-model="formData.cliente_cpf"
          :document-type="selectedDocumentType"
          :label="currentDocumentPresentation.label"
          :ng-required="false"
          data-testid="cliente-cpf-input"
          @validation="handleDocumentValidation">
        </input-document>

        <input-text
          elem-id="cliente_email"
          v-model="formData.cliente_email"
          :label="t('Email')"
          :placeholder="t('Email do cliente')"
          data-testid="cliente-email-input">
        </input-text>

        <input-text
          elem-id="cliente_profissao"
          v-model="formData.cliente_profissao"
          :label="t('Profissão')"
          :placeholder="t('Profissão do cliente')"
          data-testid="cliente-profissao-input">
        </input-text>

        <input-date
          elem-id="cliente_data_nascimento"
          v-model="formData.cliente_data_nascimento"
          :label="t('Data de nascimento')"
          :placeholder="t('Data de nascimento')"
          data-testid="cliente-data-nascimento-input">
        </input-date>

        <select-list
          :label="t('Origem')"
          :placeholder="t('Escolha a origem')"
          elem-id="cliente_origem_id"
          v-model="formData.cliente_origem_id"
          :itens-select="origens"
          data-testid="cliente-origem-select">
        </select-list>

        <select-list
          :label="t('Status')"
          :placeholder="t('Escolha o status')"
          elem-id="cliente_status"
          v-model="formData.cliente_status"
          :itens-select="statusLista"
          data-testid="cliente-status-select">
        </select-list>
      </box>

      <!-- Phone -->
      <box type="primary" :title="t('Telefone')" elem-id="boxTelefone">
        <input-phone
          :label="t('Telefone')"
          :ng-required="true"
          elem-id="cliente_telefone"
          v-model="phoneModel"
          :default-country="phoneCountryCode"
          :language="language"
          data-testid="cliente-telefone-input">
        </input-phone>
      </box>

      <!-- Address -->
      <box type="primary" :title="t('Endereço')" elem-id="boxEndereco">
        <location-select
          :label="t('Localidade')"
          :placeholder="t('Escolha a localidade')"
          elem-id="cliente_location"
          v-model:location-id="formData.cliente_location_id"
          v-model:location-type="formData.cliente_location_type"
          :franchise-id="formData.cliente_franquia_id"
          :is-ready="locationSelectReady"
          :auto-select-first="false"
        />
        <!-- CEP - Apenas para países com postal code -->
        <input-text
          v-if="countryConfig.hasPostalCode"
          elem-id="cliente_cep"
          v-model="formData.cliente_cep"
          :label="countryConfig.postalCodeLabel"
          :placeholder="countryConfig.postalCodeLabel"
          data-testid="cliente-cep-input"
          @input="handleCepInput">
        </input-text>
        <!-- Rua/Calle - Label dinâmico -->
        <input-text
          elem-id="cliente_rua"
          v-model="formData.cliente_rua"
          :label="countryConfig.streetLabel"
          :placeholder="countryConfig.streetLabel"
          data-testid="cliente-rua-input">
        </input-text>
        <!-- Número -->
        <input-text
          elem-id="cliente_numero"
          v-model="formData.cliente_numero"
          :label="t('Número')"
          :placeholder="t('Número')"
          data-testid="cliente-numero-input">
        </input-text>

        <!-- Complemento/Depto - Label dinâmico -->
        <input-text
          elem-id="cliente_complemento"
          v-model="formData.cliente_complemento"
          :label="countryConfig.complementLabel"
          :placeholder="countryConfig.complementLabel"
          data-testid="cliente-complemento-input">
        </input-text>

        <!-- Bairro/Comuna - Label dinâmico -->
        <input-text
          elem-id="cliente_bairro"
          v-model="formData.cliente_bairro"
          :label="countryConfig.neighborhoodLabel"
          :placeholder="countryConfig.neighborhoodLabel"
          data-testid="cliente-bairro-input">
        </input-text>
      </box>
      <!-- No LGPD block here: this is the STAFF form that records a customer. The
           consent is the customer's own and belongs to a customer-facing surface,
           never to this modal (removed 2026-10-30). -->
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useClienteForm } from '../../composables/useClienteForm';
import { config } from '../../services/configService';

interface ClienteFormProps {
  clienteId?: number | string;
  usuarioVersao?: number | string;
  prefill?: Record<string, unknown> | null;
}

const props = withDefaults(defineProps<ClienteFormProps>(), {
  clienteId: 0,
  usuarioVersao: 0,
  prefill: null
});

const emit = defineEmits<{
  'save-success': [];
  'client-loaded': [];
}>();

// Observação digitada no editor inline enquanto o cliente ainda não existe
// (clienteId === 0). O modal é o dono do texto (v-model:pending-observation);
// o composable grava a ocorrência logo depois do POST clientes/save e zera o
// ref — por isso é um model gravável, não um computed sobre a prop.
const pendingObservation = defineModel<string>('pendingObservation', { default: '' });

const { t } = useI18n();

const {
  formData, phoneModel, origens, statusLista,
  clienteCompletionData, locationSelectReady,

  countryConfig, documentConfig, selectedDocumentType,
  currentDocumentPresentation, hasMultipleDocumentTypes,
  documentTypeOptions, phoneCountryCode,

  language,

  init, loadCliente, saveCliente, validateNome,
  handleCepInput, handleFranquiaData, handleFranquiaChange,
  handleDocumentTypeChange, handleDocumentValidation,

  retornaCliente, extraInformationsObject,
  inferSuccessCallback, inferErrorCallback,
} = useClienteForm({
  clienteId: computed(() => Number(props.clienteId)) as unknown as number,
  prefill: computed(() => props.prefill),
  pendingObservation,
  emitFn: emit
});

const handleSubmit = async (): Promise<void> => {
  await saveCliente();
};

onMounted(() => {
  init(props.clienteId);
  if (config.isDevelopment) {
    (window as unknown as { __clienteForm: unknown }).__clienteForm = { handleFranquiaData, formData };
  }
});

defineExpose({
  save: saveCliente,
  loadCliente,
  handleFranquiaData
});
</script>

<style scoped>
.cliente-form {
  padding: 15px;
}

.ai-completion-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f0f8ff;
  border-radius: 4px;
  border: 1px solid #b0d4f1;
}
</style>
